import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Users, MapPin, Calendar, Building2, Plane, Hotel, Bus, Globe, Headphones, Code, PenTool, Award, CheckCircle } from 'lucide-react';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1626707647519-939352c5a37d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxQb3J0dWdhbCUyMFNwYWluJTIwY29hc3QlMjBhZXJpYWwlMjBzdW5ueSUyMG9jZWFufGVufDB8fHx8MTc3MTcxNDI4MHww&ixlib=rb-4.1.0&q=85';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function Home() {
  const { t } = useLanguage();

  const stats = [
    { value: '10+', label: t('home.statsYears'), icon: Calendar },
    { value: '500+', label: t('home.statsPartners'), icon: Building2 },
    { value: '100+', label: t('home.statsDestinations'), icon: MapPin },
    { value: '6000+', label: t('home.statsGroups'), icon: Users },
  ];

  const services = [
    { icon: Headphones, title: t('services.booking.title'), desc: t('services.booking.desc') },
    { icon: Globe, title: t('services.support.title'), desc: t('services.support.desc') },
    { icon: Users, title: t('services.commercial.title'), desc: t('services.commercial.desc') },
    { icon: Code, title: t('services.technical.title'), desc: t('services.technical.desc') },
    { icon: PenTool, title: t('services.marketing.title'), desc: t('services.marketing.desc') },
    { icon: Award, title: t('services.quality.title'), desc: t('services.quality.desc') },
  ];

  const products = [
    { icon: Users, title: t('products.groups.title'), link: '/products' },
    { icon: Hotel, title: t('products.hotels.title'), link: '/products' },
    { icon: Bus, title: t('products.circuits.title'), link: '/products' },
    { icon: Plane, title: t('products.flights.title'), link: '/products' },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Portugal Spain Coast"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-[#00D4FF]/20 text-[#00D4FF] rounded-full text-sm font-medium mb-6">
                7x7 Travel
              </span>
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {t('hero.title')}
            </motion.h1>

            <motion.p
              className="text-lg text-slate-300 mb-8 leading-relaxed"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t('hero.subtitle')}
            </motion.p>

            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/group-request">
                <Button
                  size="lg"
                  className="bg-[#00D4FF] hover:bg-[#00D4FF]/90 text-[#0F172A] rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="hero-cta-btn"
                >
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold"
                  data-testid="hero-secondary-btn"
                >
                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('home.statsTitle')}
            </h2>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 hover:shadow-xl transition-shadow duration-300"
                variants={fadeInUp}
                data-testid={`stat-${index}`}
              >
                <stat.icon className="w-10 h-10 text-[#00D4FF] mx-auto mb-4" />
                <div className="text-4xl sm:text-5xl font-bold text-[#1E3A8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-slate-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('home.servicesTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('home.servicesSubtitle')}
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="group p-8 bg-white rounded-2xl border border-slate-100 hover:border-[#00D4FF]/30 hover:shadow-xl transition-all duration-300"
                variants={fadeInUp}
                whileHover={{ y: -5 }}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#00D4FF] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <service.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="text-center mt-12"
            {...fadeInUp}
          >
            <Link to="/services">
              <Button
                size="lg"
                className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-full px-8"
              >
                {t('hero.ctaSecondary')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Products Preview */}
      <section className="py-24 bg-white" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('home.productsTitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('home.productsSubtitle')}
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {products.map((product, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
              >
                <Link to={product.link}>
                  <div className="group relative p-8 bg-gradient-to-br from-[#1E3A8A] to-[#1E3A8A]/80 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#00D4FF]/20 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500" />
                    <product.icon className="w-12 h-12 text-[#00D4FF] mb-4 relative z-10" />
                    <h3 className="text-xl font-bold text-white relative z-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {product.title}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-white/70 mt-4 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            {...fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('home.ctaTitle')}
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              {t('home.ctaSubtitle')}
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#00D4FF] hover:bg-[#00D4FF]/90 text-[#0F172A] rounded-full px-8 py-6 text-lg font-semibold"
                data-testid="cta-contact-btn"
              >
                {t('home.ctaButton')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
